import { HotelOfferPriceBreakdown, HotelPriceDetails } from '../models';

export enum PromoType {
  ExclusiveRate = 'exclusiveRate',
}

export function getPromotions(tags: string[] = []) {
  const promoMap = new Map();

  const promotions = tags
    .map((promo) => promo.split('promo:'))
    .reduce((accumulator, value) => accumulator.concat(value), [])
    .filter((promo) => promo.length);

  promotions.forEach((value) => {
    if (+value > 0) {
      promoMap.set(PromoType.ExclusiveRate, +value);
    }
  });

  return promoMap;
}

export function getHotelOfferPriceBreakdown(
  fallbackPrice: number,
  nights: number,
  priceDetails?: HotelPriceDetails,
  tags: string[] = [],
  isStandardRate: boolean = false,
): HotelOfferPriceBreakdown {
  const allPromotinos = getPromotions(tags);
  const exclusiveDealDiscount = allPromotinos.has(PromoType.ExclusiveRate)
    ? allPromotinos.get(PromoType.ExclusiveRate)
    : null;
  const hasExclusiveDeal = allPromotinos.has(PromoType.ExclusiveRate) || isStandardRate;

  if (!priceDetails) {
    return {
      exclusiveDealDiscount,
      hasExclusiveDeal,
      hasSaverPlus: false,
      roomexPrice: fallbackPrice / nights,
      supplierPrice: 0,
      saverPlusDiscount: 0,
      priceWithoutTaxesAndFees: undefined,
    };
  }

  const { price, sellPrice, saverPlusFee, supplierBasePrice } = priceDetails;

  const priceWithDiscount = price || sellPrice;
  const hasNegativeDiscount = !!priceWithDiscount && priceWithDiscount >= supplierBasePrice;
  const hasSaverPlus = !hasExclusiveDeal && !hasNegativeDiscount && saverPlusFee > 0;
  const supplierPrice = supplierBasePrice / nights;
  const roomexPrice = (priceWithDiscount || fallbackPrice) / nights;
  const saverPlusDiscount = supplierPrice - roomexPrice;
  const priceWithoutTaxesAndFees = priceDetails.priceBreakdown?.priceWithoutTaxesAndFees;

  return {
    exclusiveDealDiscount,
    hasExclusiveDeal,
    hasSaverPlus,
    roomexPrice,
    supplierPrice,
    saverPlusDiscount,
    priceWithoutTaxesAndFees: priceWithoutTaxesAndFees || 0 / nights,
  };
}

export function hasOfferSaverPlus(offer: { priceDetails?: HotelPriceDetails }): boolean {
  if (!offer || !offer.priceDetails) {
    return false;
  }
  const { supplierBasePrice, price, sellPrice, saverPlusFee } = offer.priceDetails;
  const priceWithDiscount = price || sellPrice;
  const hasNegativeDiscount = !!priceWithDiscount && priceWithDiscount > supplierBasePrice;
  return saverPlusFee > 0 && !hasNegativeDiscount;
}

export function totalOffersWithSaverPlus(offers: { priceDetails?: HotelPriceDetails }[]): number {
  return offers.reduce((total, offer) => {
    return hasOfferSaverPlus(offer) ? total + 1 : total;
  }, 0);
}
